import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ShieldCheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';
import { Button } from '@components/ui';
import { trackingHelpers } from '@/services/mixpanel';

interface ElectronicSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHaveCertificate: () => void;
  onNeedCertificate: () => void;
}

export function ElectronicSignatureModal({
  isOpen,
  onClose,
  onHaveCertificate,
  onNeedCertificate,
}: ElectronicSignatureModalProps) {
  // Track modal opened
  useEffect(() => {
    if (isOpen) {
      trackingHelpers.trackCertificateModal.opened('invoice-wizard');
    }
  }, [isOpen]);

  const handleHaveCertificate = () => {
    trackingHelpers.trackCertificateModal.hasSignature('invoice-wizard');
    onHaveCertificate();
  };

  const handleNeedCertificate = () => {
    trackingHelpers.trackCertificateModal.needsSignature('invoice-wizard');
    onNeedCertificate();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-secondary-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-full">
                      <ShieldCheckIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-secondary-900 dark:text-white"
                      >
                        ¿Ya tienes una firma electrónica activa?
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Subtitle */}
                <div className="mb-6">
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Es un paso indispensable para emitir facturas electrónicas. Si aún no la tienes,
                    puedes tramitarla con nuestro equipo.
                  </p>
                </div>

                {/* Alert info */}
                <div className="mb-6 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <DocumentCheckIcon className="h-5 w-5 text-info-600 dark:text-info-400 mt-0.5" />
                    <div className="text-sm text-info-800 dark:text-info-200">
                      <p className="font-medium mb-1">¿Qué es la firma electrónica?</p>
                      <p className="text-info-700 dark:text-info-300">
                        Es el certificado digital que autentica tus facturas electrónicas ante la DGI de Panamá.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center"
                    onClick={handleHaveCertificate}
                    leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                  >
                    Sí, ya la tengo
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full justify-center"
                    onClick={handleNeedCertificate}
                  >
                    No tengo, necesito tramitarla
                  </Button>
                </div>

                {/* Footer note */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Si seleccionas "Sí, ya la tengo", verifica que tu certificado esté correctamente configurado.
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
